export type SaveMethod = 'share' | 'download';

/**
 * iPhone / iPad — including iPadOS that reports as MacIntel with touch.
 */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || isIOS();
}

export function mimeForFilename(filename: string, fallback?: string): string {
  if (fallback) return fallback;
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  switch (ext) {
    case 'mp4':
      return 'video/mp4';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    case 'gif':
      return 'image/gif';
    case 'zip':
      return 'application/zip';
    default:
      return 'application/octet-stream';
  }
}

function canShareFile(file: File): boolean {
  try {
    return (
      typeof navigator !== 'undefined' &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [file] })
    );
  } catch {
    return false;
  }
}

/**
 * Same-origin blob + download attribute. Works on desktop and Android Chrome.
 * iOS Safari often ignores this for video/* and plays the file instead, which
 * is why iOS prefers the Web Share path in saveBlob().
 */
export function triggerAnchorDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  window.setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 2000);
}

/**
 * Save a blob as close to a real "Download" as the browser allows.
 *
 * - iOS: Web Share sheet (Save Video / Save Image → Photos) when the browser
 *   still has user activation; otherwise a same-origin blob download.
 * - Android / desktop: programmatic <a download> on a blob: URL.
 */
export async function saveBlob(
  blob: Blob,
  filename: string,
  mimeType?: string
): Promise<SaveMethod> {
  const type = mimeForFilename(filename, mimeType || blob.type || undefined);
  const typed = blob.type === type ? blob : new Blob([blob], { type });
  const file = new File([typed], filename, { type });

  if (isIOS() && canShareFile(file)) {
    try {
      await navigator.share({ files: [file], title: filename });
      return 'share';
    } catch (err) {
      const name = err instanceof Error ? err.name : '';
      // User closed the sheet — don't also start a download.
      if (name === 'AbortError') return 'share';
      // NotAllowedError (lost tap activation) or unsupported: fall through.
    }
  }

  // Safari may inline-play video/mp4 blobs; octet-stream treats it as a file.
  const downloadBlob =
    isIOS() && type.startsWith('video/')
      ? new Blob([typed], { type: 'application/octet-stream' })
      : typed;

  triggerAnchorDownload(downloadBlob, filename);
  return 'download';
}

export function shareHintMessage(filename: string): string {
  return `Choose Save Video or Save Image in the share sheet to add ${filename} to Photos`;
}
